import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { InlineAudioLane } from '../timelineModel';
import { captionDisplayEnd, captionDurationLabel } from '../captionGrouping';
import { captionFontForTimelineZoom, clampTimelineZoom, MAX_TIMELINE_ZOOM } from './TimelineZoomControls';
import CachedRangePreviewFrame, {
  RANGE_PREVIEW_FRAME_STATE,
  RangePreviewFrame,
  RangePreviewFrameLoadState,
} from './CachedRangePreviewFrame';
import { useVideoEditingFontScale } from '../fontScale';

// iPhone-style trim selector: a thick track representing the whole clip, with a
// draggable handle on either end framing the selected region (bright border,
// everything outside dimmed) and a thin playhead line.
interface RangeTrimmerProps {
  duration: number;      // full clip length (sec)
  inSec: number;         // selection start
  outSec: number;        // selection end
  playheadSec: number;   // current player time (drawn as a line)
  // Fired while dragging; `movedThumb` 0 = left handle, 1 = right handle (so the
  // caller can scrub the player to the handle being dragged, like iOS).
  onChange: (inSec: number, outSec: number, movedThumb: 0 | 1) => void;
  // Fired once when a handle drag (or keyboard nudge) finishes, with the final
  // range — the commit point for callers that apply trims on release instead of
  // via an explicit button (the aligned B-roll editor).
  onChangeEnd?: (inSec: number, outSec: number, movedThumb: 0 | 1) => void;
  // A plain click moves only the playback head; it must not reposition either
  // trim handle.
  onSeek: (timeSec: number) => void;
  // Optional audio clips projected onto this same source-time scale. Used by the
  // timeline video editor to show spoken annotations beneath the video rail.
  audioLanes?: InlineAudioLane[];
  pixelsPerSecond?: number;
  onZoomChange?: (zoom: number) => void;
  focusRangeKey?: string;
  snapPoints?: number[];
  snapOnDrop?: boolean;
  trackHeight?: number;
  videoFrames?: Array<RangePreviewFrame | null>;
  loadVideoFrames?: boolean;
  // Optional slide gesture: dragging the selection BODY moves the whole range
  // (both handles together, width preserved) — how NLEs reposition an overlay
  // clip along the timeline. Fired continuously while dragging; a plain click
  // inside the selection still seeks. Arrow keys on the body nudge the range.
  onRangeMove?: (inSec: number, outSec: number) => void;
  // Fired once on release with the final position (the commit point).
  onRangeMoveEnd?: (inSec: number, outSec: number) => void;
  // Accessible name for the movable selection body (e.g. "Move B-roll clip").
  rangeMoveLabel?: string;
  // Confine the filmstrip to this sub-range of the track instead of the full
  // width (percent-positioned, so moving the range is pure CSS — the frames
  // themselves never regenerate while a bar slides). Used by the aligned B-roll
  // editor, whose frames are anchored to the clip bar rather than the axis.
  // Not combined with template-style (sourceId) frames.
  filmstripRange?: { startSec: number; endSec: number };
  // Fill colour of the selected region. Defaults to a faint amber wash; the
  // aligned editor passes 'transparent' so the kept frames show cleanly (the
  // trimmed-off handles are dimmed instead).
  selectionFill?: string;
}

const DEFAULT_TRACK_HEIGHT = 40;
const AUDIO_LANE_HEIGHT = 38;
const HANDLE_WIDTH = 16;
const MIN_GAP_SEC = 0.2;
const DRAG_THRESHOLD_PX = 3;
const KEYBOARD_STEP_SEC = 0.05;
const ACCENT = '#F59E0B';

interface DragGesture {
  pointerId: number;
  thumb: 0 | 1 | 'move';
  startClientX: number;
  trackRect: DOMRect;
  startInSec: number;
  startOutSec: number;
  currentInSec: number;
  currentOutSec: number;
  duration: number;
  moved: boolean;
  captureTarget: HTMLElement;
}

interface FilmstripViewport {
  trackWidth: number;
  viewportWidth: number;
  scrollLeft: number;
}

export interface FilmstripCell {
  frame: RangePreviewFrame | null;
  left: number;
  width: number;
}

export function visibleSourceFilmstripCells(
  template: RangePreviewFrame,
  trackWidth: number,
  viewportWidth: number,
  scrollLeft: number,
  trackHeight: number,
): FilmstripCell[] {
  if (!template.sourceId || !template.sourceDurationSec || !template.width || !template.height) return [];
  const frameWidth = Math.max(1, trackHeight * (template.width / template.height));
  const totalFrames = Math.max(1, Math.ceil(trackWidth / frameWidth));
  const firstVisible = Math.max(0, Math.floor(scrollLeft / frameWidth) - 1);
  const lastVisible = Math.min(totalFrames - 1, Math.ceil((scrollLeft + viewportWidth) / frameWidth) + 1);
  const frames: FilmstripCell[] = [];
  for (let index = firstVisible; index <= lastVisible; index += 1) {
    const left = index * frameWidth;
    const centerRatio = Math.min(1, (left + frameWidth / 2) / trackWidth);
    // Never request the exact end of the clip — that timestamp lands at/just past
    // the last frame and fails to decode, leaving a permanently dark last cell.
    const sourceTimeSec = Math.min(centerRatio * template.sourceDurationSec, Math.max(0, template.sourceDurationSec - 0.05));
    frames.push({
      left,
      width: frameWidth,
      frame: {
        ...template,
        id: `${template.sourceId}:${sourceTimeSec.toFixed(2)}`,
        sourceTimeSec,
      },
    });
  }
  return frames;
}

export default function RangeTrimmer({
  duration,
  inSec,
  outSec,
  playheadSec,
  onChange,
  onSeek,
  audioLanes = [],
  pixelsPerSecond,
  onZoomChange,
  focusRangeKey,
  snapPoints = [],
  snapOnDrop = false,
  trackHeight = DEFAULT_TRACK_HEIGHT,
  videoFrames = [],
  loadVideoFrames = true,
  onChangeEnd,
  onRangeMove,
  onRangeMoveEnd,
  rangeMoveLabel,
  filmstripRange,
  selectionFill,
}: RangeTrimmerProps) {
  const fontScale = useVideoEditingFontScale();
  const trackRef = React.useRef<HTMLDivElement | null>(null);
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const selectionRef = React.useRef<HTMLDivElement | null>(null);
  const dragRef = React.useRef<DragGesture | null>(null);
  const focusedRangeKeyRef = React.useRef<string | null>(null);
  const pendingCenterKeyRef = React.useRef<string | null>(null);
  const [filmstripViewport, setFilmstripViewport] = React.useState<FilmstripViewport>({
    trackWidth: 0,
    viewportWidth: 0,
    scrollLeft: 0,
  });
  const [failedFrameIds, setFailedFrameIds] = React.useState<Set<string>>(() => new Set());
  const callbacksRef = React.useRef({ onChange, onChangeEnd, onSeek, snapPoints, snapOnDrop, onRangeMove, onRangeMoveEnd });
  callbacksRef.current = { onChange, onChangeEnd, onSeek, snapPoints, snapOnDrop, onRangeMove, onRangeMoveEnd };

  const inPct = duration > 0 ? Math.min(100, Math.max(0, (inSec / duration) * 100)) : 0;
  const outPct = duration > 0 ? Math.min(100, Math.max(0, (outSec / duration) * 100)) : 100;
  const playheadPct = duration > 0 ? Math.min(100, Math.max(0, (playheadSec / duration) * 100)) : 0;
  const visibleAudioLanes = audioLanes.filter((lane) => lane.endSec > 0 && lane.startSec < duration);
  const zoomedTrackWidth = pixelsPerSecond == null ? null : Math.max(0, duration * pixelsPerSecond);
  const annotationFontSize = (pixelsPerSecond == null ? 9 : captionFontForTimelineZoom(pixelsPerSecond)) * fontScale;
  const percentAt = (timeSec: number) => duration > 0
    ? Math.min(100, Math.max(0, (timeSec / duration) * 100))
    : 0;

  const sourceFrameTemplate = React.useMemo(() => {
    const frames = videoFrames.filter((frame): frame is RangePreviewFrame => frame != null);
    const first = frames[0];
    if (
      !first?.sourceId
      || !first.sourceDurationSec
      || !first.width
      || !first.height
      || frames.length !== videoFrames.length
      || frames.some((frame) => (
        frame.sourceId !== first.sourceId
        || frame.src !== first.src
        || frame.sourceDurationSec !== first.sourceDurationSec
        || frame.width !== first.width
        || frame.height !== first.height
      ))
    ) return null;
    return first;
  }, [videoFrames]);

  const filmstripCells = React.useMemo<FilmstripCell[]>(() => {
    const { trackWidth, viewportWidth, scrollLeft } = filmstripViewport;
    if (sourceFrameTemplate && (trackWidth <= 0 || viewportWidth <= 0)) return [];
    if (!sourceFrameTemplate) {
      const cellWidth = trackWidth > 0 && videoFrames.length > 0 ? trackWidth / videoFrames.length : 0;
      return videoFrames.map((frame, index) => ({ frame, left: index * cellWidth, width: cellWidth }));
    }

    // Keep each thumbnail at the source video's real portrait/landscape ratio.
    // Only create cells in and just outside the visible viewport, so zoomed
    // timelines can show more frames without loading hundreds of videos.
    return visibleSourceFilmstripCells(
      sourceFrameTemplate,
      trackWidth,
      viewportWidth,
      scrollLeft,
      trackHeight,
    );
  }, [filmstripViewport, sourceFrameTemplate, trackHeight, videoFrames]);
  const visibleFrameIds = filmstripCells.flatMap(({ frame }) => (frame ? [frame.id] : []));
  // The strip stays up as long as any cell can render — an individual failed
  // cell just shows dark. Only an entirely-failed strip falls back to the
  // striped placeholder.
  const filmstripFailed = visibleFrameIds.length > 0 && visibleFrameIds.every((id) => failedFrameIds.has(id));
  // The strip always occupies its full height, even before any frame has loaded,
  // so the track doesn't grow as thumbnails arrive — that height jump was what
  // made the clip editor visibly reflow ("spaz") on open. The dark backdrop just
  // fills in with frames in place.
  const displayedTrackHeight = trackHeight;
  const combinedTrackHeight = displayedTrackHeight + visibleAudioLanes.length * AUDIO_LANE_HEIGHT;
  const handleFrameLoadStateChange = React.useCallback((
    frameId: string,
    state: RangePreviewFrameLoadState,
  ) => {
    setFailedFrameIds((current) => {
      const alreadyFailed = current.has(frameId);
      const failed = state === RANGE_PREVIEW_FRAME_STATE.FAILED;
      if (alreadyFailed === failed) return current;
      const next = new Set(current);
      if (failed) next.add(frameId);
      else next.delete(frameId);
      return next;
    });
  }, []);

  const centerSelectedRange = () => {
    const viewport = viewportRef.current;
    const selection = selectionRef.current;
    if (!viewport || !selection) return;
    const viewportRect = viewport.getBoundingClientRect();
    const selectionRect = selection.getBoundingClientRect();
    const selectionCenter = viewport.scrollLeft + selectionRect.left - viewportRect.left + selectionRect.width / 2;
    const maxScrollLeft = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
    viewport.scrollLeft = Math.max(0, Math.min(maxScrollLeft, selectionCenter - viewport.clientWidth / 2));
  };

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || pixelsPerSecond == null || !onZoomChange) return undefined;
    const handleWheel = (event: WheelEvent) => {
      if (!event.metaKey && !event.ctrlKey) return;
      event.preventDefault();
      const rect = viewport.getBoundingClientRect();
      const pointerX = event.clientX - rect.left;
      const currentWidth = trackRef.current?.getBoundingClientRect().width || viewport.clientWidth;
      const timeAtPointer = duration > 0
        ? Math.max(0, Math.min(duration, ((viewport.scrollLeft + pointerX) / currentWidth) * duration))
        : 0;
      const wheelFactor = Math.min(1.35, Math.max(0.74, Math.exp(-event.deltaY * 0.0025)));
      const next = clampTimelineZoom(pixelsPerSecond * wheelFactor);
      if (next === pixelsPerSecond) return;
      onZoomChange(next);
      requestAnimationFrame(() => {
        const nextWidth = trackRef.current?.getBoundingClientRect().width || viewport.clientWidth;
        viewport.scrollLeft = Math.max(0, (timeAtPointer / Math.max(duration, 0.001)) * nextWidth - pointerX);
      });
    };
    viewport.addEventListener('wheel', handleWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', handleWheel);
  }, [duration, pixelsPerSecond, onZoomChange]);

  React.useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return undefined;
    let animationFrame: number | undefined;
    const update = () => {
      if (animationFrame != null) cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const next = {
          trackWidth: track.getBoundingClientRect().width,
          viewportWidth: viewport.clientWidth,
          scrollLeft: viewport.scrollLeft,
        };
        setFilmstripViewport((current) => (
          Math.abs(current.trackWidth - next.trackWidth) < 0.5
          && Math.abs(current.viewportWidth - next.viewportWidth) < 0.5
          && Math.abs(current.scrollLeft - next.scrollLeft) < 0.5
            ? current
            : next
        ));
      });
    };
    update();
    viewport.addEventListener('scroll', update, { passive: true });
    let resizeObserver: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(update);
      resizeObserver.observe(viewport);
      resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      if (animationFrame != null) cancelAnimationFrame(animationFrame);
      viewport.removeEventListener('scroll', update);
      resizeObserver?.disconnect();
      if (!resizeObserver) window.removeEventListener('resize', update);
    };
  }, [duration, pixelsPerSecond, trackHeight, videoFrames.length]);

  React.useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !focusRangeKey || !onZoomChange || focusedRangeKeyRef.current === focusRangeKey) return undefined;
    let firstAnimationFrame: number | undefined;
    let secondAnimationFrame: number | undefined;
    let resizeObserver: ResizeObserver | undefined;
    const focusSelection = () => {
      const viewportWidth = viewport.clientWidth;
      if (viewportWidth <= 0 || focusedRangeKeyRef.current === focusRangeKey) return false;
      focusedRangeKeyRef.current = focusRangeKey;
      pendingCenterKeyRef.current = focusRangeKey;
      // Fit the WHOLE clip (its full duration) to the viewport so the editor
      // opens with no horizontal scrollbar. A long clip may need a zoom below the
      // normal 10 px/s floor to fit, so we clamp only the upper bound here.
      //
      // Rounded DOWN, not to nearest. The track is `duration * pixelsPerSecond` wide, so
      // rounding up by half a pixel widens it by half a pixel times the whole duration —
      // which passes the 32px the handles were allowed for on anything over about a
      // minute, and the margin is only a few pixels wide near the zoom ceiling anyway.
      // Undershooting costs a hair of width; overshooting costs the scrollbar this fit
      // exists to prevent.
      const fit = (viewportWidth - HANDLE_WIDTH * 2) / Math.max(0.2, duration);
      onZoomChange(Math.min(MAX_TIMELINE_ZOOM, Math.max(2, Math.floor(fit))));
      // If the calculated zoom already matches the current zoom there may be no
      // React render to trigger the layout effect below, so keep a post-layout
      // fallback. Two frames lets MUI and the scroll container settle first.
      firstAnimationFrame = requestAnimationFrame(() => {
        secondAnimationFrame = requestAnimationFrame(() => {
          if (pendingCenterKeyRef.current !== focusRangeKey) return;
          centerSelectedRange();
          pendingCenterKeyRef.current = null;
        });
      });
      return true;
    };
    if (!focusSelection() && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        if (focusSelection()) resizeObserver?.disconnect();
      });
      resizeObserver.observe(viewport);
    }
    return () => {
      resizeObserver?.disconnect();
      if (firstAnimationFrame != null) cancelAnimationFrame(firstAnimationFrame);
      if (secondAnimationFrame != null) cancelAnimationFrame(secondAnimationFrame);
    };
  }, [focusRangeKey, inSec, onZoomChange, outSec, duration]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useLayoutEffect(() => {
    if (!focusRangeKey || pendingCenterKeyRef.current !== focusRangeKey) return undefined;
    const animationFrame = requestAnimationFrame(() => {
      if (pendingCenterKeyRef.current !== focusRangeKey) return;
      centerSelectedRange();
      pendingCenterKeyRef.current = null;
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [duration, focusRangeKey, pixelsPerSecond]); // eslint-disable-line react-hooks/exhaustive-deps

  const timeAtClientX = (clientX: number, rect: DOMRect, clipDuration = duration) => {
    if (rect.width <= 0) return;
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    return ratio * clipDuration;
  };

  const seekAtClientX = (clientX: number) => {
    const rect = trackRef.current?.getBoundingClientRect();
    if (!rect) return;
    const time = timeAtClientX(clientX, rect);
    if (time != null) onSeek(time);
  };

  React.useEffect(() => {
    const move = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      if (!drag.moved && Math.abs(event.clientX - drag.startClientX) < DRAG_THRESHOLD_PX) return;

      drag.moved = true;
      event.preventDefault();
      const pointedTime = timeAtClientX(event.clientX, drag.trackRect, drag.duration);
      if (pointedTime == null) return;
      if (drag.thumb === 'move') {
        // Slide: shift the whole range, width preserved, clamped to the track.
        const startPointedTime = timeAtClientX(drag.startClientX, drag.trackRect, drag.duration) ?? pointedTime;
        const width = drag.startOutSec - drag.startInSec;
        const nextIn = Math.max(0, Math.min(drag.startInSec + (pointedTime - startPointedTime), drag.duration - width));
        drag.currentInSec = nextIn;
        drag.currentOutSec = nextIn + width;
        callbacksRef.current.onRangeMove?.(drag.currentInSec, drag.currentOutSec);
        return;
      }
      if (drag.thumb === 0) {
        drag.currentInSec = Math.max(0, Math.min(pointedTime, drag.startOutSec - MIN_GAP_SEC));
        callbacksRef.current.onChange(drag.currentInSec, drag.currentOutSec, 0);
      } else {
        drag.currentOutSec = Math.min(drag.duration, Math.max(pointedTime, drag.startInSec + MIN_GAP_SEC));
        callbacksRef.current.onChange(drag.currentInSec, drag.currentOutSec, 1);
      }
    };

    const finish = (event: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      dragRef.current = null;
      if (drag.captureTarget.hasPointerCapture?.(drag.pointerId)) {
        drag.captureTarget.releasePointerCapture?.(drag.pointerId);
      }
      if (!drag.moved) {
        const time = timeAtClientX(event.clientX, drag.trackRect, drag.duration);
        if (time != null) callbacksRef.current.onSeek(time);
      } else if (drag.thumb === 'move') {
        callbacksRef.current.onRangeMoveEnd?.(drag.currentInSec, drag.currentOutSec);
      } else {
        let finalIn = drag.currentInSec;
        let finalOut = drag.currentOutSec;
        if (callbacksRef.current.snapOnDrop && callbacksRef.current.snapPoints.length > 0) {
          const current = drag.thumb === 0 ? finalIn : finalOut;
          const candidates = callbacksRef.current.snapPoints.filter((point) => (
            point >= 0
            && point <= drag.duration
            && (drag.thumb === 0 ? point <= finalOut - MIN_GAP_SEC : point >= finalIn + MIN_GAP_SEC)
          ));
          if (candidates.length > 0) {
            const snapped = candidates.reduce((nearest, point) => (
              Math.abs(point - current) < Math.abs(nearest - current) ? point : nearest
            ));
            if (drag.thumb === 0) finalIn = snapped;
            else finalOut = snapped;
            callbacksRef.current.onChange(finalIn, finalOut, drag.thumb);
          }
        }
        callbacksRef.current.onChangeEnd?.(finalIn, finalOut, drag.thumb);
      }
    };

    const cancel = (event: PointerEvent) => {
      if (dragRef.current?.pointerId === event.pointerId) dragRef.current = null;
    };

    document.addEventListener('pointermove', move, { passive: false });
    document.addEventListener('pointerup', finish);
    document.addEventListener('pointercancel', cancel);
    return () => {
      document.removeEventListener('pointermove', move);
      document.removeEventListener('pointerup', finish);
      document.removeEventListener('pointercancel', cancel);
    };
    // The listeners read current callbacks from refs so an active drag survives
    // the parent renders caused by each range update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startHandleGesture = (thumb: 0 | 1 | 'move', event: React.PointerEvent<HTMLElement>) => {
    const trackRect = trackRef.current?.getBoundingClientRect();
    if (!trackRect || trackRect.width <= 0) return;
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture?.(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      thumb,
      startClientX: event.clientX,
      trackRect,
      startInSec: inSec,
      startOutSec: outSec,
      currentInSec: inSec,
      currentOutSec: outSec,
      duration,
      moved: false,
      captureTarget: event.currentTarget,
    };
  };

  const changeWithKeyboard = (thumb: 0 | 1, event: React.KeyboardEvent<HTMLElement>) => {
    let next: number | null = null;
    const step = event.shiftKey ? 0.5 : KEYBOARD_STEP_SEC;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') next = (thumb === 0 ? inSec : outSec) - step;
    if (event.key === 'ArrowRight' || event.key === 'ArrowUp') next = (thumb === 0 ? inSec : outSec) + step;
    if (event.key === 'Home') next = thumb === 0 ? 0 : inSec + MIN_GAP_SEC;
    if (event.key === 'End') next = thumb === 0 ? outSec - MIN_GAP_SEC : duration;
    if (next == null) return;
    event.preventDefault();
    if (thumb === 0) {
      const nextIn = Math.max(0, Math.min(next, outSec - MIN_GAP_SEC));
      onChange(nextIn, outSec, 0);
      onChangeEnd?.(nextIn, outSec, 0);
    } else {
      const nextOut = Math.min(duration, Math.max(next, inSec + MIN_GAP_SEC));
      onChange(inSec, nextOut, 1);
      onChangeEnd?.(inSec, nextOut, 1);
    }
  };

  return (
    <Box ref={viewportRef} data-testid="range-trimmer-viewport" sx={{ overflowX: zoomedTrackWidth == null ? 'visible' : 'auto', pb: zoomedTrackWidth == null ? 0 : 0.5 }}>
      <Box sx={{ position: 'relative', px: `${HANDLE_WIDTH / 2}px`, width: zoomedTrackWidth == null ? 'auto' : `${zoomedTrackWidth + HANDLE_WIDTH}px`, minWidth: '100%', boxSizing: 'border-box' }}>
        <Box
          ref={trackRef}
          data-testid="range-trimmer-track"
          onClick={(event) => seekAtClientX(event.clientX)}
          sx={{
            position: 'relative',
            height: combinedTrackHeight,
            borderRadius: '8px',
            cursor: 'pointer',
            touchAction: 'pan-y',
            overflow: 'hidden',
            backgroundColor: '#e2e8f0',
          }}
        >
        {videoFrames.length > 0 && !filmstripFailed ? (
          <Box
            data-testid="range-trimmer-filmstrip"
            sx={{
              position: 'absolute',
              top: 0,
              left: filmstripRange ? `${percentAt(filmstripRange.startSec)}%` : 0,
              width: filmstripRange
                ? `${Math.max(0, percentAt(filmstripRange.endSec) - percentAt(filmstripRange.startSec))}%`
                : '100%',
              height: displayedTrackHeight,
              backgroundColor: '#334155',
              overflow: 'hidden',
            }}
          >
            {filmstripRange
              // Ranged strips lay their cells out proportionally INSIDE the
              // positioned container, so a bar slide moves the strip via CSS
              // without touching any frame.
              ? videoFrames.map((frame, index) => (
                <Box
                  key={frame?.id || `empty-frame-${index}`}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${(index / videoFrames.length) * 100}%`,
                    width: `${100 / videoFrames.length}%`,
                    overflow: 'hidden',
                  }}
                >
                  {frame && (
                    <CachedRangePreviewFrame
                      frame={frame}
                      loadEnabled={loadVideoFrames}
                      onLoadStateChange={handleFrameLoadStateChange}
                    />
                  )}
                </Box>
              ))
              : filmstripCells.map(({ frame, left, width }, index) => (
                <Box
                  key={frame?.id || `empty-frame-${index}`}
                  sx={{ position: 'absolute', top: 0, bottom: 0, left, width, overflow: 'hidden' }}
                >
                  {frame && (
                    <CachedRangePreviewFrame
                      frame={frame}
                      loadEnabled={loadVideoFrames}
                      onLoadStateChange={handleFrameLoadStateChange}
                    />
                  )}
                </Box>
              ))}
          </Box>
        ) : (
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: filmstripRange ? `${percentAt(filmstripRange.startSec)}%` : 0,
            width: filmstripRange
              ? `${Math.max(0, percentAt(filmstripRange.endSec) - percentAt(filmstripRange.startSec))}%`
              : '100%',
            height: displayedTrackHeight,
            background: 'repeating-linear-gradient(90deg, #334155 0 6px, #3f4f66 6px 12px)',
            transition: 'height 160ms ease',
          }} />
        )}

        {/* When the filmstrip spans more than the selection (the full source clip
            is shown behind a trimmed range), wash out the trimmed-off handles so
            the active trim stays bright while the original clip length is still
            visible. */}
        {filmstripRange && (() => {
          const selStartPct = percentAt(inSec);
          const selEndPct = percentAt(outSec);
          const filmStartPct = percentAt(filmstripRange.startSec);
          const filmEndPct = percentAt(filmstripRange.endSec);
          const scrim = { position: 'absolute' as const, top: 0, height: displayedTrackHeight, backgroundColor: 'rgba(248, 250, 252, 0.62)', pointerEvents: 'none' as const, zIndex: 1 };
          return (
            <>
              {filmStartPct < selStartPct - 0.01 && (
                <Box data-testid="range-trimmer-handle-dim-start" sx={{ ...scrim, left: `${filmStartPct}%`, width: `${selStartPct - filmStartPct}%` }} />
              )}
              {filmEndPct > selEndPct + 0.01 && (
                <Box data-testid="range-trimmer-handle-dim-end" sx={{ ...scrim, left: `${selEndPct}%`, width: `${filmEndPct - selEndPct}%` }} />
              )}
            </>
          );
        })()}

        {visibleAudioLanes.map((lane, laneIndex) => {
          const laneStart = Math.max(0, lane.startSec);
          const laneEnd = Math.min(duration, lane.endSec);
          const laneLeftPct = percentAt(laneStart);
          const laneWidthPct = Math.max(0, percentAt(laneEnd) - laneLeftPct);
          return (
            <Box
              key={lane.id}
              data-testid={`range-trimmer-audio-lane-${lane.id}`}
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: displayedTrackHeight + laneIndex * AUDIO_LANE_HEIGHT,
                height: AUDIO_LANE_HEIGHT,
                borderTop: '1px solid rgba(15, 23, 42, 0.2)',
                backgroundColor: '#ecfeff',
                overflow: 'hidden',
              }}
            >
              <Box sx={{
                position: 'absolute',
                left: `${laneLeftPct}%`,
                width: `${laneWidthPct}%`,
                top: 0,
                bottom: 0,
                backgroundColor: lane.color || '#0891b2',
                color: '#fff',
                overflow: 'hidden',
              }}>
                <Typography
                  title={lane.note ? `${lane.label} · ${lane.note}` : lane.label}
                  sx={{ px: 0.5, height: 16, fontSize: 9 * fontScale, lineHeight: '16px', fontWeight: 700, color: '#fff' }}
                  noWrap
                >
                  {lane.label}{lane.note ? ` · ${lane.note}` : ''}
                </Typography>
                {lane.annotations.map((annotation, annotationIndex) => {
                  const annotationStart = Math.max(laneStart, annotation.startSec);
                  const annotationEnd = Math.min(
                    laneEnd,
                    captionDisplayEnd(lane.annotations, annotationIndex),
                  );
                  if (annotationEnd <= annotationStart || laneEnd <= laneStart) return null;
                  const left = ((annotationStart - laneStart) / (laneEnd - laneStart)) * 100;
                  const width = ((annotationEnd - annotationStart) / (laneEnd - laneStart)) * 100;
                  const durationLabel = captionDurationLabel(annotationStart, annotationEnd);
                  return (
                    <Typography
                      key={`${annotation.startSec}-${annotationIndex}`}
                      title={`${annotation.text} ${durationLabel}`}
                      sx={{
                        position: 'absolute',
                        left: `${left}%`,
                        width: `${Math.max(width, 1.5)}%`,
                        top: 17,
                        bottom: 1,
                        px: 0.35,
                        borderLeft: '1px solid rgba(255,255,255,0.65)',
                        boxSizing: 'border-box',
                        fontSize: annotationFontSize,
                        lineHeight: '18px',
                        color: '#fff',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {annotation.text}{' '}
                      <Box component="span" sx={{ fontSize: '0.78em', fontWeight: 500, opacity: 0.72 }}>
                        {durationLabel}
                      </Box>
                    </Typography>
                  );
                })}
              </Box>
            </Box>
          );
        })}

        <Box
          ref={selectionRef}
          data-testid="range-trimmer-selection"
          {...(onRangeMove ? {
            role: 'slider',
            tabIndex: 0,
            'aria-label': rangeMoveLabel || 'Move selection',
            'aria-valuemin': 0,
            'aria-valuemax': Math.max(0, duration - (outSec - inSec)),
            'aria-valuenow': inSec,
            'aria-valuetext': `${inSec.toFixed(1)} seconds`,
            onPointerDown: (event: React.PointerEvent<HTMLElement>) => startHandleGesture('move', event),
            onClick: (event: React.MouseEvent) => event.stopPropagation(),
            onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => {
              const step = event.shiftKey ? 0.5 : KEYBOARD_STEP_SEC;
              let delta: number | null = null;
              if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') delta = -step;
              if (event.key === 'ArrowRight' || event.key === 'ArrowUp') delta = step;
              if (delta == null) return;
              event.preventDefault();
              const width = outSec - inSec;
              const nextIn = Math.max(0, Math.min(inSec + delta, duration - width));
              onRangeMove(nextIn, nextIn + width);
              onRangeMoveEnd?.(nextIn, nextIn + width);
            },
          } : {})}
          sx={{
            position: 'absolute',
            zIndex: 2,
            top: 0,
            bottom: 0,
            left: `${inPct}%`,
            width: `${Math.max(0, outPct - inPct)}%`,
            borderTop: `3px solid ${ACCENT}`,
            borderBottom: `3px solid ${ACCENT}`,
            boxSizing: 'border-box',
            backgroundColor: selectionFill ?? 'rgba(245, 158, 11, 0.18)',
            pointerEvents: onRangeMove ? 'auto' : 'none',
            ...(onRangeMove ? { cursor: 'grab', touchAction: 'none', outline: 'none', '&:active': { cursor: 'grabbing' }, '&:focus-visible': { boxShadow: `0 0 0 3px rgba(245, 158, 11, 0.35)` } } : {}),
          }}
        />

        {([0, 1] as const).map((thumb) => {
          const value = thumb === 0 ? inSec : outSec;
          const pct = thumb === 0 ? inPct : outPct;
          const label = thumb === 0 ? 'Trim start' : 'Trim end';
          return (
            <Box
              key={thumb}
              component="div"
              role="slider"
              tabIndex={0}
              aria-label={label}
              aria-valuemin={thumb === 0 ? 0 : inSec + MIN_GAP_SEC}
              aria-valuemax={thumb === 0 ? outSec - MIN_GAP_SEC : duration}
              aria-valuenow={value}
              aria-valuetext={`${value.toFixed(1)} seconds`}
              data-testid={thumb === 0 ? 'range-trimmer-start-handle' : 'range-trimmer-end-handle'}
              onPointerDown={(event) => startHandleGesture(thumb, event)}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => changeWithKeyboard(thumb, event)}
              sx={{
                position: 'absolute',
                zIndex: 4,
                top: 0,
                left: `calc(${pct}% - ${HANDLE_WIDTH / 2}px)`,
                width: HANDLE_WIDTH,
                height: combinedTrackHeight,
                borderRadius: '5px',
                backgroundColor: ACCENT,
                cursor: 'ew-resize',
                touchAction: 'none',
                outline: 'none',
                '&:hover, &:focus-visible, &:active': { boxShadow: `0 0 0 4px rgba(245, 158, 11, 0.25)` },
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  left: '50%',
                  top: '25%',
                  width: '2px',
                  height: '50%',
                  transform: 'translateX(-50%)',
                  borderRadius: '1px',
                  backgroundColor: 'rgba(255,255,255,0.9)',
                },
              }}
            />
          );
        })}

        {/* Playhead line (drawn over the track; playback loops inside the selection). */}
        <Box sx={{
          position: 'absolute',
          zIndex: 3,
          top: -3,
          bottom: -3,
          left: `${playheadPct}%`,
          width: '2px',
          transform: 'translateX(-50%)',
          backgroundColor: '#fff',
          border: '1px solid rgba(15, 23, 42, 0.55)',
          borderRadius: '2px',
          pointerEvents: 'none',
        }} />
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
          <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>0s</Typography>
          <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>{duration.toFixed(1)}s</Typography>
        </Box>
      </Box>
    </Box>
  );
}
